import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { formatRelativeTime } from '../../utils/format'
import { SkeletonGrid } from '../common/Skeleton'
import { Globe, Send, Settings, MoreHorizontal } from 'lucide-react'

// ── Gradient helper (same pattern as siteGradient in SiteManager) ─────────────
const CLIENT_GRADIENTS = [
  ['#7C5CFF', '#5B7CFF'],
  ['#A78BFA', '#7C5CFF'],
  ['#EC4899', '#A78BFA'],
  ['#FF8A65', '#F76C5E'],
  ['#2BD9A6', '#06B6D4'],
  ['#06B6D4', '#5B7CFF'],
  ['#FFB454', '#FF8A65'],
  ['#5BC0EB', '#5B7CFF'],
]

function clientGradient(client) {
  const hash = (client.id || '').toString().split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  return CLIENT_GRADIENTS[hash % CLIENT_GRADIENTS.length]
}

function getInitials(name) {
  if (!name) return '?'
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
}

// ── Status helpers ─────────────────────────────────────────────────────────────
const STATUS_TONE = {
  active: 'success',
  trial: 'warning',
  inactive: 'danger',
  suspended: 'danger',
}
const STATUS_LABEL = {
  active: 'Active',
  trial: 'Trial',
  inactive: 'Inactive',
  suspended: 'Suspended',
}

function getClientStatus(client) {
  if (!client.is_active) return 'inactive'
  if (client.billing_status === 'trialing') return 'trial'
  return 'active'
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function avatarGrad(client) {
  return ((client.id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 6) + 1
}

function ClientAvatar({ client, lg = false }) {
  const n = avatarGrad(client)
  return (
    <div className={`avatar ${lg ? 'avatar-lg' : ''} avatar-grad-${n}`}>
      {getInitials(client.company_name || client.user_name)}
    </div>
  )
}

function PlanBadge({ plan, plansRaw }) {
  const isCustom = plansRaw?.[plan]?.custom === true
  let tone = ''
  if (isCustom) tone = 'accent'
  else if (plan === 'pro' || plan === 'premium') tone = 'accent'
  const label = isCustom ? 'Custom' : (plan?.charAt(0).toUpperCase() + plan?.slice(1)) || 'Basic'
  return <span className={`badge${tone ? ' badge-' + tone : ''}`}>{label}</span>
}

function StatusPill({ client }) {
  const status = getClientStatus(client)
  const tone = STATUS_TONE[status] || ''
  return (
    <span className={`badge${tone ? ' badge-' + tone : ''}`}>
      <span className="badge-dot" />
      {STATUS_LABEL[status] || 'Unknown'}
    </span>
  )
}

function QuotaBar({ used, total }) {
  const pct = Math.min((used / (total || 1)) * 100, 100)
  const color = pct > 90 ? 'var(--error)' : pct > 70 ? 'var(--warning)' : 'var(--success)'
  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-faint)', marginBottom: 4, fontFamily: 'var(--font-mono)' }}>
        <span>Edit quota</span>
        <span>{used || 0}/{total || 10}</span>
      </div>
      <div style={{ height: 4, borderRadius: 999, background: 'var(--border)', overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 999, width: `${pct}%`, background: color, transition: 'width .3s' }} />
      </div>
    </div>
  )
}

function Mini({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 500, fontSize: 13, marginTop: 2 }}>{value}</div>
    </div>
  )
}

function ClientCard({ client, plansRaw, onDelete }) {
  const plan = plansRaw?.[client.subscription_plan]
  const planName = plan?.name || (client.subscription_plan
    ? client.subscription_plan.charAt(0).toUpperCase() + client.subscription_plan.slice(1)
    : 'Basic')
  const planPrice = plan?.price

  return (
    <div className="site-card" style={{ padding: 16 }}>
      {/* Top row: circular avatar + company name + contact · email + horizontal dots */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <ClientAvatar client={client} lg />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="site-name truncate" style={{ fontSize: 15 }}>
            {client.company_name || client.user_name}
          </div>
          <div className="truncate" style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            {client.user_name} · {client.email}
          </div>
        </div>
        <button className="site-action" title="More">
          <MoreHorizontal style={{ width: 14, height: 14 }} />
        </button>
      </div>

      {/* Badges: status, plan, price */}
      <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
        <StatusPill client={client} />
        <span className="badge">{planName}</span>
        {planPrice !== undefined && (
          <span className="badge" style={{ fontFamily: 'var(--font-mono)' }}>£{planPrice}/mo</span>
        )}
      </div>

      {/* Mini stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
        <Mini label="Sites" value={client.site_count ?? 0} />
        <Mini label="Edits" value={client.edits_used ?? 0} />
        <Mini label="Last seen" value={client.last_login ? formatRelativeTime(client.last_login) : 'Never'} />
      </div>

      {/* Footer: Sites | Message | Settings */}
      <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
        <Link
          to={`/admin/sites?clientId=${client.id}`}
          className="btn btn-sm"
          style={{ flex: 1, justifyContent: 'center', textDecoration: 'none' }}
        >
          <Globe style={{ width: 12, height: 12 }} /> Sites
        </Link>
        <Link
          to={`/admin/requests?newFor=${client.id}`}
          className="btn btn-sm"
          style={{ flex: 1, justifyContent: 'center', textDecoration: 'none' }}
        >
          <Send style={{ width: 12, height: 12 }} /> Message
        </Link>
        <Link
          to={`/admin/clients/${client.id}/edit`}
          className="site-action"
          title="Settings"
          style={{ textDecoration: 'none' }}
        >
          <Settings style={{ width: 14, height: 14 }} />
        </Link>
      </div>
    </div>
  )
}

function ClientRow({ client, plansRaw, onDelete }) {
  const plan = plansRaw?.[client.subscription_plan]
  const planName = plan?.name || (client.subscription_plan
    ? client.subscription_plan.charAt(0).toUpperCase() + client.subscription_plan.slice(1)
    : 'Basic')

  return (
    <div className="sites-table-row" style={{ gridTemplateColumns: '2fr 1fr 1fr 0.6fr 0.7fr 1fr auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <ClientAvatar client={client} />
        <div>
          <div style={{ color: 'var(--text)', fontWeight: 500, fontSize: 13.5 }}>
            {client.company_name || client.user_name}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
            {client.user_name} · {client.email}
          </div>
        </div>
      </div>
      <div><StatusPill client={client} /></div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>{planName}</span>
        {plan?.price !== undefined && (
          <span style={{ color: 'var(--text-faint)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>£{plan.price}</span>
        )}
      </div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{client.site_count ?? 0}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{client.edits_used ?? 0}/{client.edit_quota ?? 10}</div>
      <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
        {client.last_login ? formatRelativeTime(client.last_login) : 'Never'}
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        <Link to={`/admin/requests?newFor=${client.id}`} className="site-action" title="Message" style={{ textDecoration: 'none' }}>
          <Send style={{ width: 13, height: 13 }} />
        </Link>
        <Link to={`/admin/clients/${client.id}/edit`} className="site-action" title="Settings" style={{ textDecoration: 'none' }}>
          <Settings style={{ width: 13, height: 13 }} />
        </Link>
      </div>
    </div>
  )
}

function DeleteModal({ client, onConfirm, onCancel, isLoading }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  return (
    <div className="drawer-backdrop" onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-0)', border: '1px solid var(--border)', borderRadius: 16,
        padding: '28px 28px 24px', maxWidth: 420, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,.4)',
        position: 'relative', zIndex: 201,
      }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(239,68,68,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="var(--error)" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
          </div>
          <h3 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Delete Client</h3>
          <p style={{ fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Are you sure you want to delete <strong style={{ color: 'var(--text)' }}>{client.company_name || client.user_name}</strong>?
            This action cannot be undone and will delete all associated sites and data.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onCancel} disabled={isLoading}>Cancel</button>
          <button
            className="btn"
            style={{ background: 'var(--error)', color: '#fff', border: 'none' }}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Deleting…' : 'Delete Client'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────────

const ClientManager = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [view, setView] = useState('grid')
  const [clientToDelete, setClientToDelete] = useState(null)

  const queryClient = useQueryClient()

  // ── API calls (preserved exactly) ──
  const { data: clientsData, isLoading, error } = useQuery(
    ['clients'],
    () => apiService.get('/clients').then(res => res.data),
    { staleTime: 30000 }
  )
  const clients = clientsData?.clients || []

  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { staleTime: 5 * 60 * 1000 }
  )

  const deleteClientMutation = useMutation(
    (clientId) => apiService.delete(`/clients/${clientId}`),
    {
      onSuccess: () => {
        toast.success('Client deleted successfully')
        queryClient.invalidateQueries(['clients'])
        queryClient.invalidateQueries(['admin-stats'])
        queryClient.invalidateQueries('admin-quick-stats')
        queryClient.invalidateQueries(['analytics'])
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete client')
      }
    }
  )

  // ── Filtering ──
  const allClients = clients.length > 0 ? clients : (error ? [] : [])

  const filtered = allClients.filter(c => {
    const q = searchQuery.toLowerCase()
    const matchesSearch = !q ||
      c.user_name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.company_name?.toLowerCase().includes(q)

    const status = getClientStatus(c)
    const matchesStatus = filterStatus === 'all' || status === filterStatus

    return matchesSearch && matchesStatus
  })

  const counts = {
    all: allClients.length,
    active: allClients.filter(c => getClientStatus(c) === 'active').length,
    trial: allClients.filter(c => getClientStatus(c) === 'trial').length,
    inactive: allClients.filter(c => getClientStatus(c) === 'inactive').length,
  }

  const mrr = allClients
    .filter(c => getClientStatus(c) === 'active')
    .reduce((s, c) => s + (plansRaw?.[c.subscription_plan]?.price || 0), 0)

  const handleDelete = (client) => setClientToDelete(client)
  const handleDeleteConfirm = () => {
    if (clientToDelete) {
      deleteClientMutation.mutate(clientToDelete.id)
      setClientToDelete(null)
    }
  }

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Clients</div>
          <h1>Clients</h1>
          <div className="subtitle">
            {counts.all} clients
            {mrr > 0 && ` · $${mrr.toLocaleString()}/mo recurring`}
            {counts.trial > 0 && ` · ${counts.trial} in trial`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Link to="/admin/clients/new" className="btn btn-primary" style={{ textDecoration: 'none' }}>
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add client
          </Link>
        </div>
      </div>

      <div className="page-body">
        {/* Mini stats */}
        <div className="bento" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          {[
            { label: 'Total', value: counts.all, tone: '', icon: 'users' },
            { label: 'Active', value: counts.active, tone: 'success', icon: 'check' },
            { label: 'In trial', value: counts.trial, tone: 'warning', icon: 'bolt' },
            { label: 'MRR', value: mrr > 0 ? `$${mrr.toLocaleString()}` : '—', tone: 'accent', icon: 'card' },
          ].map(s => (
            <div key={s.label} className="card card-pad-sm" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span className={`icon-tile${s.tone ? ' ' + s.tone : ''}`}>
                {s.icon === 'users' && <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>}
                {s.icon === 'check' && <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
                {s.icon === 'bolt' && <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>}
                {s.icon === 'card' && <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>}
              </span>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
                <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filter bar */}
        <div className="filter-bar">
          <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--text-muted)', flexShrink: 0 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            className="input"
            placeholder="Search by name, email, company…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <div className="vdiv" style={{ height: 22 }} />
          <div className="segmented">
            {[['all', 'All', counts.all], ['active', 'Active', counts.active], ['trial', 'Trial', counts.trial], ['inactive', 'Inactive', counts.inactive]].map(([k, l, n]) => (
              <button key={k} className={filterStatus === k ? 'on' : ''} onClick={() => setFilterStatus(k)}>
                {l} <span style={{ color: 'var(--text-faint)', marginLeft: 4, fontFamily: 'var(--font-mono)' }}>{n}</span>
              </button>
            ))}
          </div>
          <div className="vdiv" style={{ height: 22 }} />
          <div className="segmented" style={{ marginLeft: 'auto' }}>
            <button className={view === 'grid' ? 'on' : ''} onClick={() => setView('grid')} title="Grid view">
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
            </button>
            <button className={view === 'list' ? 'on' : ''} onClick={() => setView('list')} title="List view">
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
            </button>
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <SkeletonGrid count={6} />
        )}

        {/* Grid view */}
        {!isLoading && view === 'grid' && filtered.length > 0 && (
          <div className="sites-grid">
            {filtered.map(c => (
              <ClientCard key={c.id} client={c} plansRaw={plansRaw} onDelete={handleDelete} />
            ))}
          </div>
        )}

        {/* List view */}
        {!isLoading && view === 'list' && filtered.length > 0 && (
          <div className="sites-table">
            <div className="sites-table-header" style={{ gridTemplateColumns: '2fr 1fr 1fr 0.6fr 0.7fr 1fr auto' }}>
              <div>Client</div>
              <div>Status</div>
              <div>Plan</div>
              <div>Sites</div>
              <div>Edits</div>
              <div>Last seen</div>
              <div />
            </div>
            {filtered.map(c => (
              <ClientRow key={c.id} client={c} plansRaw={plansRaw} onDelete={handleDelete} />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && filtered.length === 0 && (
          <div style={{ padding: '60px 0', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="var(--text-faint)" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
            </div>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
              {searchQuery || filterStatus !== 'all' ? 'No clients match your filters' : 'No clients yet'}
            </h3>
            <p style={{ fontSize: 13.5, color: 'var(--text-muted)', maxWidth: 380, margin: '0 auto 20px' }}>
              {searchQuery || filterStatus !== 'all'
                ? 'Try adjusting your search or filter.'
                : 'Add your first client to get started.'}
            </p>
            {(searchQuery || filterStatus !== 'all') && (
              <button className="btn" onClick={() => { setSearchQuery(''); setFilterStatus('all') }}>Clear filters</button>
            )}
            {!searchQuery && filterStatus === 'all' && (
              <Link to="/admin/clients/new" className="btn btn-primary" style={{ textDecoration: 'none' }}>Add client</Link>
            )}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {clientToDelete && (
        <DeleteModal
          client={clientToDelete}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setClientToDelete(null)}
          isLoading={deleteClientMutation.isLoading}
        />
      )}
    </>
  )
}

export default ClientManager
